import { Flex, RootScreen } from '@banhala/ui-kit';
import { useInstanceId, useOpenDeeplink } from 'shared/src/hooks';
import { AnalyticsScreen } from 'shared/src/context';
import { ScreenStateProvider } from './states/ScreenStateProvider';
import { searchHelperParamsAtom } from './states/searchHelperParams.atom';
import { SearchHelperHeader } from './components/SearchHelperHeader';
import { SearchHelperContents } from './components/SearchHelperContents';
import { useSetAtom } from 'jotai';
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { bridge, localStorageBridge } from 'shared/src/utils/bridge';
import { systemAlert } from 'shared/src/component/systemAlert';
import { useIsForegroundNow } from 'shared/src/hooks';
import { isWeb } from 'shared/src/utils';
import { ADSContext } from '@banhala/ui-kit';
import { useLogViewEvent } from './hook/useLogViewEvent';
import { SearchType } from 'shared/src/api/enums';
import { consumeSearchReturnData } from '../../shared/searchReturnData';
import { useSearchRoute } from './hook/useSearchRoute';
import { useNavigation, type NavigationProp } from '@react-navigation/native';
import type { SearchStackParamList } from '../../Navigator';

export type RecentKeywordItem = {
  keyword: string;
  is_delete: boolean;
};

const STORAGE_KEY = 'KEY_RECENT_SEARCH';

const SearchHelper = () => {
  useLogViewEvent();
  const { contentWidth } = useContext(ADSContext);
  const navigation = useNavigation<NavigationProp<SearchStackParamList>>();

  const setParams = useSetAtom(searchHelperParamsAtom);
  const { params } = useSearchRoute<'SearchHelper'>();
  const instanceId = useInstanceId();
  const isForeground = useIsForegroundNow();
  const { openDeeplink } = useOpenDeeplink();

  /** show_keyboard 1회 소비: 페이지 레벨 ref로 리마운트 방지 */
  const showKeyboardConsumedRef = useRef(false);
  let autoFocusOnMount = false;
  if (params.show_keyboard === 'true' && !showKeyboardConsumedRef.current) {
    showKeyboardConsumedRef.current = true;
    autoFocusOnMount = true;
  }

  /** 최근검색어 상태 */
  const [recentKeywords, setRecentKeywords] = useState<RecentKeywordItem[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const hasLoadedOnce = useRef(false);

  /** SearchResult에서 복귀 시 검색어 + 포커스 복원 */
  const webInitialQuery = isWeb ? (params.query?.trim() || undefined) : undefined;
  const [returnQuery, setReturnQuery] = useState<string | undefined>(webInitialQuery);
  const [focusTrigger, setFocusTrigger] = useState(0);
  const [resetScrollTrigger, setResetScrollTrigger] = useState(0);

  /** 최초 로드 */
  useEffect(() => {
    if (!hasLoadedOnce.current) {
      hasLoadedOnce.current = true;
      loadRecentKeywords();
    }
  }, []);

  /** 웹: SearchResult에서 복귀 시 검색어/포커스 복원 */
  const webShowKeyboard = isWeb && params.show_keyboard === 'true';
  const webClearQuery = isWeb && params.show_keyboard === 'false';

  /** 딥링크 query가 있으면 즉시 SearchResult로 자동 이동 (iOS 동일 동작) */
  useEffect(() => {
    // 웹에서 SearchResult → 검색바 클릭으로 복귀한 경우 (show_keyboard=true) 자동 이동 스킵
    if (isWeb && params.show_keyboard === 'true') return;
    const deeplinkQuery = params.query?.trim();
    if (!deeplinkQuery) return;
    const searchType = (params.search_type as SearchType) || SearchType.DIRECT;
    navigateToSearchResult(deeplinkQuery, searchType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** SearchResult 복귀 데이터 체크 + 최근검색어 갱신 */
  useEffect(() => {
    if (!hasLoadedOnce.current) return;
    if (!isForeground) return;

    const checkReturnFromSearchResult = () => {
      if (!instanceId) return;
      const data = consumeSearchReturnData(instanceId);
      if (!data) return;
      setReturnQuery(data.query);
      setFocusTrigger(prev => prev + 1);
    };

    checkReturnFromSearchResult();
    loadRecentKeywords();
    setResetScrollTrigger(prev => prev + 1);
  }, [isForeground, instanceId]);

  /** onQueryChange 참조 안정화 (헤더 effect 불필요 재실행 방지) */
  const handleQueryChange = useCallback(
    (query: string) => {
      setParams(prev => ({ ...prev, query }));
    },
    [setParams],
  );

  /** 최근검색어 불러오기*/
  const loadRecentKeywords = async () => {
    try {
      const stored = await localStorageBridge.getString(STORAGE_KEY);
      if (!stored) return;

      const parsed: RecentKeywordItem[] = JSON.parse(stored);
      setRecentKeywords(parsed.filter(item => !item.is_delete));
    } catch (e) {
      console.warn('load recent keywords failed', e);
    }
  };

  /** 네이티브 저장 */
  const saveKeywords = async (items: RecentKeywordItem[]) => {
    await localStorageBridge.putString(STORAGE_KEY, JSON.stringify(items));
  };

  /** 최근검색어 추가 (중복 제거 + 앞에 추가 + 최대 10개) */
  const addRecentKeyword = async (keyword: string) => {
    const filtered = recentKeywords.filter(item => item.keyword !== keyword);

    const updated: RecentKeywordItem[] = [
      { keyword, is_delete: false },
      ...filtered,
    ].slice(0, 10);

    setRecentKeywords(updated);
    await saveKeywords(updated);
  };

  /** SearchResult 화면으로 이동 */
  const navigateToSearchResult = async (
    keyword: string,
    searchType: SearchType,
  ) => {
    if (isWeb) {
      navigation.navigate('SearchResult', {
        query: keyword,
        search_type: searchType,
        ...(params.delivery_type === 'fast' && {
          delivery_type: params.delivery_type,
        }),
        ...(params.from_screen && { from_screen: params.from_screen }),
      });
      return;
    }
    const extras = JSON.stringify({
      query: keyword,
      search_type: searchType,
      from_instance_id: instanceId,
      // fast 타입일 때만 포함 → FilterListAtom → API 자동 전달
      ...(params.delivery_type === 'fast' && {
        delivery_type: params.delivery_type,
      }),
      // 로깅 준비용 (API 미전달, params에만 보관)
      ...(params.from_screen && { from_screen: params.from_screen }),
    });
    const encodedExtras = encodeURIComponent(extras);
    const deeplink = `ably://react/App?screen=SearchResult&extras=${encodedExtras}`;
    openDeeplink(deeplink);
  };

  /** 키워드 선택 시 최근검색어 추가 + 검색결과 이동 */
  const handleSelectKeyword = async (
    keyword: string,
    searchType: SearchType,
  ) => {
    await addRecentKeyword(keyword);
    await navigateToSearchResult(keyword, searchType);
  };

  /** 개별 삭제 (is_delete=true 유지 저장) */
  const deleteRecentKeyword = async (keyword: string) => {
    const updated = recentKeywords.map(item =>
      item.keyword === keyword ? { ...item, is_delete: true } : item,
    );

    setRecentKeywords(updated.filter(item => !item.is_delete));
    await saveKeywords(updated.slice(0, 10));
  };

  /** 전체 삭제 */
  const clearRecentKeywords = async () => {
    systemAlert(
      '최근 검색어를 모두 삭제하시겠어요?',
      undefined,
      [
        {
          text: '취소',
          style: 'cancel',
        },
        {
          text: '확인',
          onPress: async () => {
            const updated = recentKeywords.map(item => ({
              ...item,
              is_delete: true,
            }));

            bridge.logAnalyticsEvent({
              name: 'CLICK_REMOVE_ALL_KEYWORDS',
              channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
              analytics: {
                SCREEN: 'SEARCH',
              },
            });

            setRecentKeywords([]);
            setIsEditMode(false);
            await saveKeywords(updated);
          },
        },
      ],
      { cancelable: true },
    );
  };

  return (
    <AnalyticsScreen screen="SEARCH">
    <RootScreen>
      <Flex flex={1} width={contentWidth} alignSelf="center">
      <ScreenStateProvider>
        <SearchHelperHeader
          autoFocusOnMount={autoFocusOnMount || webShowKeyboard}
          initialQuery={returnQuery ?? (webShowKeyboard ? params.query?.trim() : webClearQuery ? '' : undefined)}
          focusTrigger={focusTrigger}
          onQueryChange={handleQueryChange}
          onSubmitSearch={async query => {
            setParams(prev => ({ ...prev, query }));
            await handleSelectKeyword(query, SearchType.DIRECT);
          }}
          onPressImageSearch={() => {
            const deeplink =
              'ably://webview?url=https://m.a-bly.com/image-search&title_bar=false';
            openDeeplink(deeplink);
          }}
        />
        <SearchHelperContents
          recentKeywords={recentKeywords}
          isEditMode={isEditMode}
          onChangeEditMode={setIsEditMode}
          onSelectKeyword={handleSelectKeyword}
          onDeleteRecentKeyword={deleteRecentKeyword}
          onClearRecentKeywords={clearRecentKeywords}
          resetScrollTrigger={resetScrollTrigger}
        />
      </ScreenStateProvider>
      </Flex>
    </RootScreen>
    </AnalyticsScreen>
  );
};

export default SearchHelper;
